"use client";

import {
  memo,
  type InputHTMLAttributes,
  type ReactNode,
  useState,
  useEffect,
  type Ref,
} from "react";

import Input from "../../common/input/Input";

import Popover, {
  PopoverContent,
  PopoverTrigger,
} from "@/components/common/popover";

import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
} from "../../common/drawer/drawer";

import Flex from "../../common/Flex";
import { useDeviceType } from "@/shared/context/DeviceContext";
import { cn } from "@/shared/libs/commonUtils";

interface CustomInputProps extends InputHTMLAttributes<HTMLInputElement> {
  variant:
    | "origin"
    | "destination"
    | "checkin"
    | "checkout"
    | "date"
    | "flightType"
    | "passengers"
    | "city"
    | "from"
    | "to"
    | "rooms";
  children: ReactNode;
  className?: string;
  placeholder?: string;
  format?: () => string | undefined;
  icon?: ReactNode | ReactNode[];
  onProgressChange?: () => void;
  openStatus?: boolean;
  handleOpen?: (open: boolean) => void;
  ref?: Ref<HTMLInputElement>;
}

const widthAuto = ["to", "from", "checkin", "checkout", "date"];
const widthComfortable = ["rooms", "passengers"];

export default memo(function CustomInput({
  variant,
  className,
  icon,
  children,
  placeholder,
  value,
  format,
  onProgressChange,
  openStatus,
  handleOpen,
  ref,
  ...props
}: CustomInputProps) {
  const [open, setOpen] = useState(openStatus);
  const deviceType = useDeviceType();

  useEffect(() => {
    setOpen(openStatus);
  }, [openStatus]);

  return (
    <Flex className="flex min-w-full justify-between">
      {deviceType !== "mobile" ? (
        <Popover open={openStatus} onOpenChange={handleOpen}>
          <PopoverTrigger className="w-full">
            <Input
              {...props}
              ref={ref}
              value={format?.() ?? (value || "")}
              icon={icon}
              placeholder={placeholder}
              readOnly={true}
              aria-readonly={true}
              className={`w-full shadow-none! border-border rounded-sm text-sm font-medium tracking-wide ${
                variant === "origin" && "md:rounded-tl-none md:rounded-bl-none"
              }
              ${
                variant === "destination" &&
                "border-r-0! md:rounded-tr-none md:rounded-br-none"
              }
              ${className}`}
            />
          </PopoverTrigger>
          <PopoverContent
            className={cn(
              "rounded-xs !p-0",
              widthAuto.includes(props.name!) && "w-auto",
              widthComfortable.includes(variant) &&
                "w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-2rem)]",
            )}
          >
            {children}
          </PopoverContent>
        </Popover>
      ) : (
        <Drawer
          open={open}
          onOpenChange={(nextOpen) => {
            setOpen(nextOpen);
            handleOpen?.(nextOpen);
          }}
        >
          <DrawerTrigger className="w-full">
            <Input
              {...props}
              ref={ref}
              value={format?.() ?? (value || "")}
              icon={icon}
              readOnly={true}
              aria-readonly={true}
              placeholder={placeholder}
              // onClick={() => setOpen(true)}
              autoFocus={false}
              className={`w-full !shadow-none !border-ghost rounded-xs text-sm font-medium ${
                variant === "origin" && "md:rounded-tl-none md:rounded-bl-none"
              }
              ${
                variant === "destination" &&
                "md:border-r-0! md:rounded-tr-none md:rounded-br-none"
              }
              ${className}`}
            />
          </DrawerTrigger>
          <DrawerContent className="h-dvh max-h-svh">{children}</DrawerContent>
        </Drawer>
      )}
    </Flex>
  );
});
